import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { TaskSchedule } from '../../types/tasks';
import { useTasks } from '../../context/TasksContext';
import { taskApi, taskErrorMessage } from '../../utils/taskApi';
import { recurrenceLabel } from '../../utils/recurrence';
import { relativeDay, shortDate } from '../../utils/taskDates';
import { firstName } from '../../utils/taskUsers';
import TaskDialog from './TaskDialog';
import TaskIcon from './TaskIcon';
import { BubbleStack } from './UserBubble';
import '../../tasks.css';

/**
 * Every scheduled and recurring task in one place — including paused ones,
 * which have no dates left on the calendar to click on.
 */
const SchedulesDialog: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { users, me, today, lists, openComposer } = useTasks();
  const [schedules, setSchedules] = useState<TaskSchedule[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      setSchedules((await taskApi.calendar(today, today)).schedules);
      setError(null);
    } catch (e) {
      setError(taskErrorMessage(e, 'Could not load schedules'));
    }
  }, [today]);

  useEffect(() => {
    load();
    window.addEventListener('tasks:schedules-changed', load);
    return () => window.removeEventListener('tasks:schedules-changed', load);
  }, [load]);

  const groups = useMemo(() => {
    const all = schedules || [];
    const ended = (s: TaskSchedule) => !!s.recurrence?.until && s.recurrence.until < today;
    return [
      { key: 'recurring', title: 'Recurring', rows: all.filter((s) => s.recurrence && s.active) },
      { key: 'upcoming', title: 'Coming up', rows: all.filter((s) => !s.recurrence && s.active) },
      { key: 'paused', title: 'Paused', rows: all.filter((s) => s.recurrence && !s.active && !ended(s)) },
      { key: 'ended', title: 'Ended', rows: all.filter((s) => s.recurrence && !s.active && ended(s)) },
    ].filter((g) => g.rows.length);
  }, [schedules, today]);

  const listTitle = useMemo(() => new Map(lists.map((l) => [l.id, l.title])), [lists]);
  const where = (s: TaskSchedule) =>
    s.target === 'weekly'
      ? 'Weekly list'
      : s.target === 'new_list'
        ? 'New list'
        : `"${(s.target_list_id && listTitle.get(s.target_list_id)) || 'another list'}"`;

  const canEdit = (s: TaskSchedule) => s.created_by === me || users.find((u) => u.email === me)?.user_type === 'Admin';

  const togglePaused = async (s: TaskSchedule) => {
    setBusy(s.id);
    try {
      await taskApi.updateSchedule(s.id, { active: !s.active });
      await load();
      window.dispatchEvent(new Event('tasks:schedules-changed'));
    } catch (e) {
      setError(taskErrorMessage(e, 'Could not update the schedule'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <TaskDialog title="Scheduled & recurring" onClose={onClose} wide>
      {error && <p className="tl-error">{error}</p>}
      {!schedules && !error && <p className="tk-muted">Loading…</p>}
      {schedules && !groups.length && (
        <div className="tc-agenda-empty">
          <p>Nothing scheduled yet.</p>
          <button type="button" className="tk-btn tk-btn-ghost" onClick={() => openComposer({ when: 'repeat' })}>
            <TaskIcon name="plus" size={14} /> Schedule a task
          </button>
        </div>
      )}
      {groups.map((g) => (
        <section key={g.key} className="ts-group">
          <h4 className="tk-section-label ts-group-title">{g.title} · {g.rows.length}</h4>
          <ul className="ts-list">
            {g.rows.map((s) => {
              const when = s.recurrence
                ? `${recurrenceLabel(s.recurrence) || 'Repeats'}${s.recurrence.until ? ` until ${shortDate(s.recurrence.until)}` : ''}`
                : relativeDay(s.start_date, today);
              return (
                <li key={s.id} className={`ts-row${s.active ? '' : ' is-paused'}`}>
                  <span className={`ts-icon ts-icon-${s.recurrence ? 'recurring' : 'scheduled'}`}>
                    <TaskIcon name={s.recurrence ? 'recurring' : 'scheduled'} size={15} />
                  </span>
                  <button
                    type="button"
                    className="ts-main"
                    onClick={() => canEdit(s) && openComposer({ schedule: s })}
                    disabled={!canEdit(s)}
                    title={canEdit(s) ? 'Edit' : `Scheduled by ${firstName(users, s.created_by)}`}
                  >
                    <span className="ts-title">{s.title}</span>
                    <span className="ts-meta">
                      {when} · {where(s)}
                      {s.created_by !== me && ` · by ${firstName(users, s.created_by)}`}
                    </span>
                  </button>
                  {s.assignees.length > 0 && <BubbleStack users={users} emails={s.assignees} />}
                  {canEdit(s) && s.recurrence && g.key !== 'ended' && (
                    <button type="button" className="tk-btn tk-btn-ghost tk-btn-sm" onClick={() => togglePaused(s)} disabled={busy === s.id}>
                      {s.active ? 'Pause' : 'Resume'}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </TaskDialog>
  );
};

export default SchedulesDialog;
